import { apiSlice } from './apiSlice'

export type ReportsDashboard = {
  total_cases: number
  total_teams: number
  total_courtrooms: number
  submitted_scores_count: number
  average_total_score: number | null
  leaderboard: Array<{
    team_id: string
    team_name: string
    avg_score: number
    score_count: number
  }>
}

export type RoundCompletion = {
  case_id: string
  round_id: string
  expected_total: number
  submitted_count: number
  complete: boolean
  judges: Array<{
    judge_id: string
    judge_name: string
    judge_email?: string | null
    done: number
    expected: number
    complete: boolean
  }>
  teams: Array<{
    team_id: string
    team_name: string
    university?: string | null
  }>
}

export type VideoTiebreakBreakdown = {
  creativity: number
  presentation: number
  professionalism: number
}

export type RankingRow = {
  rank: number
  team_id: string
  team_name: string
  judge_count: number
  score_count?: number  // tổng số phiếu (memorial + evidence + ...) tích lũy
  average_score: number
  total_score: number
  video_tiebreak_total?: number
  video_tiebreak?: VideoTiebreakBreakdown
  tied_with_neighbor?: boolean
}

export type RankingResult = {
  case_id: string
  round_id: string | null
  total_teams: number
  ranking: RankingRow[]
  tie_break_applied?: boolean
}

export type NextRoundSchedule = {
  bracket_at?: string
  role_sheet_due?: string
  hearing_at?: string
}

export type PublishRoundBody = {
  case_id: string
  round_id: string
  advancing_team_ids: string[]
  title?: string
  note?: string
  next_round_label?: string
  next_round_schedule?: NextRoundSchedule
}

export type PublishRoundResult = {
  published_id: string
  case_id: string
  round_id: string
  advancing_team_ids: string[]
  notified_teams: number
  ranking: RankingRow[]
}

export const reportsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getReportsDashboard: builder.query<ReportsDashboard, void>({
      query: () => '/reports/dashboard',
    }),
    exportScoresCsv: builder.query<Blob, void>({
      query: () => ({
        url: '/reports/export/scores.csv',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    exportSummaryTxt: builder.query<string, void>({
      query: () => ({
        url: '/reports/export/summary.txt',
        responseHandler: (response: Response) => response.text(),
      }),
    }),
    exportSummaryPdf: builder.query<Blob, void>({
      query: () => ({
        url: '/reports/export/summary.pdf',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    // UAT-TC #15: kiểm tra giám khảo đã chấm xong
    getRoundCompletion: builder.query<RoundCompletion, { case_id: string; round_id: string }>({
      query: ({ case_id, round_id }) => ({
        url: '/reports/round-completion',
        params: { case_id, round_id },
      }),
    }),
    // UAT-TC #15 + Chung kết #16: ranking theo vòng (round_id=null → tổng hợp)
    getRanking: builder.query<RankingResult, { case_id: string; round_id?: string | null }>({
      query: ({ case_id, round_id }) => ({
        url: '/reports/ranking',
        params: round_id ? { case_id, round_id } : { case_id },
      }),
    }),
    // UAT-TC #16 + Bán kết #11 + Chung kết #18: công bố kết quả
    publishRound: builder.mutation<PublishRoundResult, PublishRoundBody>({
      query: (body) => ({
        url: '/reports/publish-round',
        method: 'POST',
        body,
      }),
    }),
    // Tie-break Điều 10.3 — chấm Video clip
    setVideoTiebreak: builder.mutation<
      any,
      { team_id: string; creativity: number; presentation: number; professionalism: number }
    >({
      query: ({ team_id, ...body }) => ({
        url: `/teams/${team_id}/video-tiebreak`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (res, err, { team_id }) => [
        { type: 'Team', id: team_id },
        'Team',
      ],
    }),
    // Public Guest endpoints
    getPublicResultsCases: builder.query<
      Array<{ case_id: string; title: string; case_type?: string | null }>,
      void
    >({
      query: () => '/public/results/cases',
    }),
    getPublicLatestResults: builder.query<
      {
        case_id: string
        case_title: string
        round: { round_name: string; round_type: string } | null
        published_at: string
        title: string
        note: string
        total_teams: number
        advancing_count: number
        advancing_teams: Array<{
          team_name: string
          university: string
          competition_status: string | null
        }>
      },
      string
    >({
      query: (caseId) => `/public/results/${caseId}/latest`,
    }),
    getPublicVerdicts: builder.query<
      Array<{
        courtroom_id: string
        round_name: string | null
        round_type: string | null
        verdict_summary: string | null
        verdict_legal_basis: string | null
        verdict_penalty: string | null
        winner_team: { team_id: string; team_name: string } | null
        published_at: string | null
      }>,
      string
    >({
      query: (caseId) => `/public/verdicts/${caseId}`,
    }),
  }),
})

export const {
  useGetReportsDashboardQuery,
  useLazyExportScoresCsvQuery,
  useLazyExportSummaryTxtQuery,
  useLazyExportSummaryPdfQuery,
  useGetRoundCompletionQuery,
  useLazyGetRoundCompletionQuery,
  useGetRankingQuery,
  useLazyGetRankingQuery,
  usePublishRoundMutation,
  useSetVideoTiebreakMutation,
  useGetPublicResultsCasesQuery,
  useGetPublicLatestResultsQuery,
  useGetPublicVerdictsQuery,
} = reportsApi
